// src/pages/Hub/theme.js
//
// Shared design language for the Operations Hub: colour tokens, region + status
// metadata, and the single injected stylesheet (HUB_CSS). Kept in one place so every
// hub component speaks the same visual language.

export const C = {
  bg0: '#060A12', bg1: '#0A111E', bg2: '#0F1A2E',
  surface: 'rgba(255,255,255,0.045)', surface2: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.08)', borderHi: 'rgba(56,189,248,0.5)',
  text: '#E8EEF7', muted: '#93A3B8', faint: '#5B6B82',
  cyan: '#38BDF8', indigo: '#6366F1', green: '#34D399',
  violet: '#A78BFA', amber: '#F59E0B', red: '#F87171', orange: '#FB923C',
};

// Region identity (drives globe pin colour, badges, legend).
export const REGION_META = {
  residential: { label: 'Residential',            short: 'RES', color: '#34D399' },
  commercial:  { label: 'Commercial / Industrial', short: 'C&I', color: '#38BDF8' },
  mideast:     { label: 'Mideast (NOVAA)',         short: 'ME',  color: '#A78BFA' },
};
export const regionMeta = (k) =>
  REGION_META[k] || { label: k || 'Unknown', short: String(k || '?').slice(0, 3).toUpperCase(), color: '#94A3B8' };
export const REGION_ORDER = ['residential', 'commercial', 'mideast'];

// Job status → label + colour, spanning all three regions' status vocabularies.
const STATUS = {
  scheduled:     { label: 'Scheduled',     color: '#38BDF8' },
  pending:       { label: 'Pending',       color: '#38BDF8' },
  dispatched:    { label: 'Dispatched',    color: '#22D3EE' },
  in_progress:   { label: 'In Progress',   color: '#F59E0B' },
  started:       { label: 'Started',       color: '#F59E0B' },
  reached_site:  { label: 'Reached Site',  color: '#FBBF24' },
  reached_panels:{ label: 'Reached Panels',color: '#FBBF24' },
  work_started:  { label: 'Work Started',  color: '#F59E0B' },
  before_photos: { label: 'Before Photos', color: '#A78BFA' },
  after_photos:  { label: 'After Photos',  color: '#A78BFA' },
  work_finished: { label: 'Work Finished', color: '#A78BFA' },
  completed:     { label: 'Completed',     color: '#34D399' },
  cancelled:     { label: 'Cancelled',     color: '#F87171' },
  rescheduled:   { label: 'Rescheduled',   color: '#FB923C' },
  missed:        { label: 'Missed',        color: '#F87171' },
};
export const statusInfo = (s) => {
  if (!s) return { label: '—', color: '#64748B' };
  return STATUS[String(s).toLowerCase()] || { label: s, color: '#94A3B8' };
};

// Range presets for the top control.
export const RANGE_PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: '7 days', days: 6 },
  { key: '30d', label: '30 days', days: 29 },
  { key: 'mtd', label: 'MTD', days: null },
];
export function rangeFor(presetKey) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (presetKey === 'mtd') return { from: `${to.slice(0, 7)}-01`, to };
  const preset = RANGE_PRESETS.find((p) => p.key === presetKey) || RANGE_PRESETS[2];
  const d = new Date(now);
  d.setDate(d.getDate() - (preset.days || 0));
  return { from: d.toISOString().slice(0, 10), to };
}

export const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(0);
};

export const HUB_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

.hub-root, .hub-root * { box-sizing: border-box; }
.hub-root {
  font-family: 'Space Grotesk', sans-serif; color: ${C.text};
  /* own scroll container so the page scrolls even if an ancestor clips overflow */
  height: 100vh; overflow-y: auto; overflow-x: hidden;
  padding: 18px 20px 36px;
  background:
    radial-gradient(1100px 520px at 78% -8%, rgba(99,102,241,0.16), transparent),
    radial-gradient(900px 500px at 12% 8%, rgba(56,189,248,0.10), transparent),
    linear-gradient(180deg, ${C.bg0}, ${C.bg1});
  background-attachment: fixed;
}

/* Top bar */
.hub-top { display:flex; align-items:center; gap:18px; margin-bottom:16px; flex-wrap:wrap; }
.hub-brand { display:flex; align-items:center; gap:12px; }
.hub-brand-badge { display:grid; place-items:center; width:40px; height:40px; border-radius:12px;
  background:linear-gradient(135deg, ${C.cyan}, ${C.indigo}); color:#04121f; box-shadow:0 8px 26px rgba(56,189,248,0.35); }
.hub-brand-name { font-size:19px; font-weight:700; letter-spacing:-0.01em; line-height:1.1; }
.hub-brand-sub { font-size:11px; color:${C.muted}; font-family:'DM Mono', monospace; letter-spacing:0.05em; text-transform:uppercase; }

.hub-cmd { flex:1; min-width:240px; max-width:560px; display:flex; align-items:center; gap:11px; cursor:text;
  background:${C.surface}; border:1px solid ${C.border}; border-radius:14px; padding:12px 15px; transition:border-color .15s, box-shadow .15s; }
.hub-cmd:hover { border-color:${C.borderHi}; box-shadow:0 0 0 3px rgba(56,189,248,0.10); }
.hub-cmd svg { color:${C.muted}; }
.hub-cmd-text { flex:1; color:${C.faint}; font-size:14px; }
.hub-cmd-kbd { display:inline-flex; gap:3px; }
.hub-cmd-kbd span { font-family:'DM Mono', monospace; font-size:11px; color:${C.muted};
  background:${C.surface2}; border:1px solid ${C.border}; border-radius:6px; padding:2px 7px; }

.hub-clock { display:flex; flex-direction:column; align-items:flex-end; }
.hub-clock-time { font-family:'DM Mono', monospace; font-size:15px; font-weight:500; }
.hub-clock-date { font-size:11px; color:${C.muted}; }
.hub-live { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; color:${C.green};
  font-family:'DM Mono', monospace; }
.hub-live-dot { width:7px; height:7px; border-radius:50%; background:${C.green}; box-shadow:0 0 8px ${C.green}; animation:hubBlink 1.6s ease-in-out infinite; }
@keyframes hubBlink { 0%,100%{opacity:1} 50%{opacity:.25} }

/* Range presets */
.hub-ranges { display:flex; gap:4px; padding:4px; border-radius:12px; background:${C.surface}; border:1px solid ${C.border}; }
.hub-range { position:relative; border:none; background:transparent; color:${C.muted}; cursor:pointer;
  padding:7px 13px; border-radius:8px; font-size:12.5px; font-weight:600; font-family:'Space Grotesk', sans-serif; }
.hub-range.is-active { color:#04121f; }
.hub-range-inner { position:relative; z-index:1; }
.hub-range-pill { position:absolute; inset:0; border-radius:8px; background:linear-gradient(135deg, ${C.cyan}, #7dd3fc); box-shadow:0 6px 16px rgba(56,189,248,0.4); }

/* Main grid */
.hub-main { display:grid; grid-template-columns:330px 1fr; gap:16px; }
@media (max-width: 1040px){ .hub-main { grid-template-columns:1fr; } }

/* Cards */
.hub-card { background:${C.surface}; border:1px solid ${C.border}; border-radius:18px; padding:18px;
  box-shadow:0 18px 50px rgba(0,0,0,0.4); }
.hub-card-title { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:700; letter-spacing:0.06em;
  text-transform:uppercase; color:${C.muted}; margin-bottom:14px; }

/* Health rail */
.hub-rail { display:flex; flex-direction:column; gap:14px; }
.hub-ring-wrap { display:flex; align-items:center; gap:16px; }
.hub-ring { position:relative; width:116px; height:116px; flex:none; }
.hub-ring-num { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
.hub-ring-score { font-size:30px; font-weight:700; line-height:1; }
.hub-ring-label { font-size:10px; color:${C.muted}; text-transform:uppercase; letter-spacing:0.08em; margin-top:3px; }
.hub-kpis { display:flex; flex-direction:column; gap:9px; flex:1; }
.hub-kpi { display:flex; align-items:center; justify-content:space-between; }
.hub-kpi-label { display:flex; align-items:center; gap:7px; font-size:12.5px; color:${C.muted}; }
.hub-kpi-val { font-family:'DM Mono', monospace; font-size:16px; font-weight:500; }

.hub-stat-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:4px; }
.hub-stat { background:${C.surface2}; border:1px solid ${C.border}; border-radius:13px; padding:12px 13px; }
.hub-stat-num { font-size:21px; font-weight:700; font-family:'DM Mono', monospace; }
.hub-stat-cap { font-size:10.5px; color:${C.muted}; text-transform:uppercase; letter-spacing:0.05em; margin-top:2px; }

.hub-regions { display:flex; flex-direction:column; gap:10px; }
.hub-region-row { }
.hub-region-head { display:flex; align-items:center; justify-content:space-between; font-size:12.5px; margin-bottom:5px; }
.hub-region-name { display:flex; align-items:center; gap:7px; font-weight:600; }
.hub-region-dot { width:9px; height:9px; border-radius:50%; box-shadow:0 0 8px currentColor; }
.hub-region-meta { font-family:'DM Mono', monospace; font-size:11.5px; color:${C.muted}; }
.hub-bar { height:7px; border-radius:6px; background:rgba(255,255,255,0.07); overflow:hidden; }
.hub-bar-fill { height:100%; border-radius:6px; }

/* Globe panel */
.hub-globe-wrap { position:relative; border-radius:18px; overflow:hidden; border:1px solid ${C.border};
  background:radial-gradient(700px 500px at 50% 40%, rgba(56,189,248,0.07), transparent), #04070E;
  box-shadow:0 24px 60px rgba(0,0,0,0.5); min-height:440px; }
.hub-globe-canvas { position:absolute; inset:0; }
.hub-globe-overlay { position:absolute; inset:0; pointer-events:none; }
.hub-globe-overlay > * { pointer-events:auto; }
.hub-globe-counter { position:absolute; top:14px; left:16px; display:flex; align-items:center; gap:7px;
  background:rgba(6,10,18,0.78); backdrop-filter:blur(8px); border:1px solid ${C.border}; color:${C.cyan};
  padding:7px 13px; border-radius:20px; font-size:11.5px; font-weight:700; font-family:'DM Mono', monospace; }
.hub-globe-regions { position:absolute; top:14px; right:16px; display:flex; flex-direction:column; gap:7px; align-items:flex-end; }
.hub-rchip { display:inline-flex; align-items:center; gap:7px; cursor:pointer; white-space:nowrap;
  background:rgba(6,10,18,0.78); backdrop-filter:blur(8px); border:1px solid ${C.border}; color:${C.text};
  padding:6px 12px; border-radius:20px; font-size:11.5px; font-weight:600; transition:all .15s; }
.hub-rchip:hover { transform:translateX(-2px); }
.hub-rchip.off { opacity:0.4; }
.hub-rchip-dot { width:9px; height:9px; border-radius:50%; box-shadow:0 0 8px currentColor; }
.hub-globe-hint { position:absolute; bottom:14px; left:16px; font-size:10.5px; color:${C.faint};
  font-family:'DM Mono', monospace; background:rgba(6,10,18,0.6); padding:5px 10px; border-radius:10px; }

/* Lower grid */
.hub-lower { display:grid; grid-template-columns:1.1fr 1fr; gap:16px; margin-top:16px; }
@media (max-width: 1040px){ .hub-lower { grid-template-columns:1fr; } }

/* Pulse feed */
.hub-feed { max-height:360px; overflow-y:auto; display:flex; flex-direction:column; gap:8px; padding-right:3px; }
.hub-feed-row { display:flex; align-items:center; gap:12px; cursor:pointer; text-align:left; width:100%;
  background:${C.surface2}; border:1px solid ${C.border}; border-radius:12px; padding:11px 13px; color:${C.text}; transition:border-color .14s, transform .14s; }
.hub-feed-row:hover { border-color:rgba(255,255,255,0.18); transform:translateX(2px); }
.hub-feed-rdot { width:10px; height:10px; border-radius:50%; flex:none; box-shadow:0 0 8px currentColor; }
.hub-feed-main { flex:1; min-width:0; }
.hub-feed-name { font-weight:600; font-size:13.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hub-feed-sub { font-size:11.5px; color:${C.muted}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hub-feed-status { font-size:11px; font-weight:700; white-space:nowrap; }
.hub-feed-empty, .hub-feed-loading { display:flex; flex-direction:column; align-items:center; gap:8px; color:${C.muted}; padding:36px 10px; text-align:center; }

/* Reports deck */
.hub-reports { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.hub-report { background:${C.surface2}; border:1px solid ${C.border}; border-radius:14px; padding:14px; position:relative; overflow:hidden; }
.hub-report-head { display:flex; align-items:center; gap:9px; font-size:12px; font-weight:700; color:${C.muted}; text-transform:uppercase; letter-spacing:0.05em; margin-bottom:10px; }
.hub-report-big { font-size:26px; font-weight:700; font-family:'DM Mono', monospace; line-height:1; }
.hub-report-sub { font-size:11.5px; color:${C.muted}; margin-top:4px; }
.hub-report-chips { display:flex; flex-wrap:wrap; gap:6px; margin-top:11px; }
.hub-rpill { font-size:10.5px; font-weight:600; padding:3px 9px; border-radius:8px; background:rgba(255,255,255,0.05); color:${C.muted}; }
.hub-report.is-click { cursor:pointer; transition:border-color .14s, box-shadow .14s, background .14s; }
.hub-report.is-click:hover { border-color:${C.borderHi}; background:${C.surface2}; box-shadow:0 0 0 3px rgba(56,189,248,0.08); }
.hub-report.is-click:focus-visible { outline:none; border-color:${C.borderHi}; box-shadow:0 0 0 3px rgba(56,189,248,0.18); }
.hub-report-go { margin-left:auto; opacity:0.55; transition:transform .14s, opacity .14s; }
.hub-report.is-click:hover .hub-report-go { opacity:1; transform:translateX(2px); }

/* Detail drawer — report breakdown + records */
.hub-detail { display:flex; flex-direction:column; }
.hub-dsub { display:flex; align-items:center; gap:8px; font-size:11px; font-weight:700; letter-spacing:0.06em; text-transform:uppercase;
  color:${C.faint}; margin:16px 0 8px; }
.hub-dsub:first-child { margin-top:6px; }
.hub-dsub-records { padding-top:14px; border-top:1px solid ${C.border}; }
.hub-dsub-n { margin-left:auto; font-family:'DM Mono', monospace; font-size:11px; font-weight:500; color:${C.muted};
  background:${C.surface2}; border:1px solid ${C.border}; border-radius:7px; padding:1px 8px; letter-spacing:0; }
.hub-dl { display:flex; flex-direction:column; gap:2px; }
.hub-dl-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:9px 12px;
  background:${C.surface2}; border:1px solid ${C.border}; border-radius:11px; }
.hub-dl-row.is-total { background:transparent; border-color:transparent; font-weight:700; }
.hub-dl-row.is-total .hub-dl-v { color:${C.text}; }
.hub-dl-k { display:flex; align-items:center; gap:8px; font-size:13px; color:${C.text}; min-width:0; }
.hub-dl-n { font-family:'DM Mono', monospace; font-size:11px; color:${C.muted}; }
.hub-dl-v { font-family:'DM Mono', monospace; font-size:13.5px; font-weight:500; color:${C.muted}; white-space:nowrap; }
.hub-dl-empty { font-size:12.5px; color:${C.muted}; padding:8px 12px; }
.hub-dl-note { font-size:12px; color:${C.faint}; line-height:1.5; padding:8px 2px 2px; }

.hub-rec { display:flex; align-items:center; gap:12px; padding:11px 13px; margin-bottom:8px;
  background:${C.surface2}; border:1px solid ${C.border}; border-radius:12px; }
.hub-rec-dot { width:9px; height:9px; border-radius:50%; flex:none; box-shadow:0 0 8px currentColor; }
.hub-rec-main { flex:1; min-width:0; }
.hub-rec-title { font-size:13.5px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hub-rec-sub { font-size:11.5px; color:${C.muted}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-top:2px; }
.hub-rec-side { display:flex; flex-direction:column; align-items:flex-end; gap:2px; flex:none; }
.hub-rec-amt { font-family:'DM Mono', monospace; font-size:13.5px; font-weight:500; }
.hub-rec-status { font-size:10.5px; font-weight:700; white-space:nowrap; }
.hub-rec.is-click { cursor:pointer; transition:border-color .14s, background .14s, transform .14s; }
.hub-rec.is-click:hover { border-color:${C.borderHi}; background:${C.surface2}; transform:translateX(2px); }
.hub-rec.is-click:focus-visible { outline:none; border-color:${C.borderHi}; }
.hub-rec-go { color:${C.faint}; flex:none; transition:color .14s, transform .14s; }
.hub-rec.is-click:hover .hub-rec-go { color:${C.cyan}; transform:translateX(2px); }

/* FSR detail sub-panel */
.hub-back { display:inline-flex; align-items:center; gap:7px; background:${C.surface2}; border:1px solid ${C.border};
  color:${C.muted}; cursor:pointer; padding:7px 12px; border-radius:10px; font-size:12.5px; font-weight:600;
  font-family:'Space Grotesk', sans-serif; margin-bottom:14px; transition:border-color .14s, color .14s; }
.hub-back:hover { border-color:${C.borderHi}; color:${C.text}; }
.hub-fsr-head { display:flex; align-items:center; gap:11px; margin-bottom:6px; }
.hub-fsr-title { font-size:15.5px; font-weight:700; }
.hub-fsr-sub { font-size:12px; color:${C.muted}; margin-top:2px; }
.hub-note-block { background:${C.surface2}; border:1px solid ${C.border}; border-radius:11px; padding:11px 13px;
  font-size:13px; line-height:1.55; color:${C.text}; margin-bottom:8px; white-space:pre-wrap; word-break:break-word; }
.hub-note-block b { color:${C.muted}; font-weight:600; }
.hub-img-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.hub-img { position:relative; display:block; border:1px solid ${C.border}; border-radius:12px; overflow:hidden;
  background:${C.surface2}; aspect-ratio:4/3; transition:border-color .14s, transform .14s; }
.hub-img:hover { border-color:${C.borderHi}; transform:translateY(-2px); }
.hub-img img { width:100%; height:100%; object-fit:cover; display:block; }
.hub-img-cap { position:absolute; left:0; right:0; bottom:0; padding:5px 9px; font-size:10.5px; font-weight:600;
  color:#E8EEF7; background:linear-gradient(180deg, transparent, rgba(3,6,12,0.82)); }

/* Command palette */
.hub-palette-scrim { position:fixed; inset:0; z-index:2000; background:rgba(3,6,12,0.62); backdrop-filter:blur(6px);
  display:flex; align-items:flex-start; justify-content:center; padding-top:12vh; }
.hub-palette { width:min(640px, 92vw); background:${C.bg1}; border:1px solid ${C.border}; border-radius:18px; overflow:hidden;
  box-shadow:0 40px 90px rgba(0,0,0,0.6); }
.hub-palette-field { display:flex; align-items:center; gap:12px; padding:18px 20px; border-bottom:1px solid ${C.border}; }
.hub-palette-field svg { color:${C.cyan}; flex:none; }
.hub-palette-field input { flex:1; background:transparent; border:none; outline:none; color:${C.text}; font-size:17px; font-family:'Space Grotesk', sans-serif; }
.hub-palette-field input::placeholder { color:${C.faint}; }
.hub-palette-esc { font-family:'DM Mono', monospace; font-size:11px; color:${C.muted}; border:1px solid ${C.border}; border-radius:6px; padding:2px 7px; }
.hub-palette-list { max-height:50vh; overflow-y:auto; }
.hub-palette-hint { padding:26px 20px; text-align:center; color:${C.faint}; font-size:13px; }
.hub-palette-group { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; color:${C.faint};
  padding:11px 20px 5px; }
.hub-res { display:flex; align-items:center; gap:12px; width:100%; text-align:left; cursor:pointer;
  background:transparent; border:none; padding:11px 20px; color:${C.text}; }
.hub-res.active, .hub-res:hover { background:${C.surface2}; }
.hub-res-icon { display:grid; place-items:center; width:34px; height:34px; border-radius:10px; flex:none; font-weight:700; font-size:11px; font-family:'DM Mono', monospace; }
.hub-res-main { flex:1; min-width:0; }
.hub-res-name { font-weight:600; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hub-res-sub { font-size:11.5px; color:${C.muted}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hub-res-badge { font-size:10px; font-weight:700; padding:3px 9px; border-radius:8px; flex:none; }
.hub-palette-foot { display:flex; gap:16px; padding:10px 20px; border-top:1px solid ${C.border}; font-size:11px; color:${C.faint}; }
.hub-palette-foot kbd { font-family:'DM Mono', monospace; background:${C.surface2}; border:1px solid ${C.border}; border-radius:5px; padding:1px 6px; }

/* Detail drawer */
.hub-drawer-scrim { position:fixed; inset:0; z-index:1500; background:rgba(3,6,12,0.5); backdrop-filter:blur(3px); }
.hub-drawer { position:fixed; top:0; right:0; bottom:0; z-index:1600; width:min(460px, 94vw); overflow-y:auto;
  background:linear-gradient(180deg, ${C.bg2}, ${C.bg1}); border-left:1px solid ${C.border}; box-shadow:-30px 0 80px rgba(0,0,0,0.6); padding:22px; }
.hub-drawer-head { display:flex; align-items:flex-start; gap:12px; margin-bottom:16px; }
.hub-drawer-x { margin-left:auto; background:transparent; border:none; color:${C.muted}; cursor:pointer; }
.hub-drawer-x:hover { color:${C.text}; }
.hub-drawer-title { font-size:18px; font-weight:700; }
.hub-drawer-sub { font-size:12px; color:${C.muted}; margin-top:2px; }
.hub-date-btn { display:inline-flex; align-items:center; gap:9px; cursor:pointer; background:${C.surface}; border:1px solid ${C.border};
  color:${C.text}; padding:9px 14px; border-radius:12px; font-size:13px; font-weight:600; font-family:'DM Mono', monospace; }
.hub-date-btn:hover { border-color:${C.borderHi}; }

.hub-job { position:relative; overflow:hidden; background:${C.surface}; border:1px solid ${C.border}; border-radius:14px;
  padding:13px 15px 13px 17px; margin-top:11px; }
.hub-job-bar { position:absolute; left:0; top:0; bottom:0; width:4px; }
.hub-job-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:7px; }
.hub-job-status { display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:700; }
.hub-job-dot { width:8px; height:8px; border-radius:50%; box-shadow:0 0 8px currentColor; }
.hub-job-title { font-size:14.5px; font-weight:700; margin-bottom:7px; }
.hub-job-meta { display:flex; flex-wrap:wrap; gap:7px 14px; }
.hub-job-meta span { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:${C.muted}; }

/* misc */
.hub-spin { animation:hubSpin 0.9s linear infinite; }
@keyframes hubSpin { to { transform:rotate(360deg); } }
.hub-fade-children > * { animation:hubFade 0.5s ease both; }
@keyframes hubFade { from { opacity:0; transform:translateY(10px);} to { opacity:1; transform:none;} }
.hub-root ::-webkit-scrollbar { width:8px; height:8px; }
.hub-root ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.13); border-radius:8px; }

/* react-datepicker dark (drawer) */
.hub-cal.react-datepicker { background:${C.bg1}; border:1px solid ${C.border}; border-radius:14px; font-family:'Space Grotesk', sans-serif; box-shadow:0 24px 50px rgba(0,0,0,0.55); overflow:hidden; color:${C.text}; }
.hub-cal .react-datepicker__header { background:${C.surface}; border-bottom:1px solid ${C.border}; }
.hub-cal .react-datepicker__current-month, .hub-cal .react-datepicker__day-name { color:${C.text}; }
.hub-cal .react-datepicker__day { color:${C.muted}; border-radius:8px; }
.hub-cal .react-datepicker__day:hover { background:${C.surface2}; color:${C.text}; }
.hub-cal .react-datepicker__day--selected, .hub-cal .react-datepicker__day--keyboard-selected { background:linear-gradient(135deg, ${C.cyan}, ${C.indigo}); color:#04121f; font-weight:700; }
.hub-cal .react-datepicker__triangle { display:none; }
`;
